import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import {
  useAssignShift, useAttendanceGaps, useDecideShiftRequest, useEndAssignment, useMarkAttendance,
  usePublishAssignment, useRoster, useShiftRequests,
} from "../../lib/api";
import { SectionCard, Stat } from "./shared";

/** The roster, the requests, and the days nobody marked (HRX-02, OS-0624's remainder).
 *
 * Three things a shift system needs and this site had none of. The numbers on arrival: 104 of 137 active people
 * rostered and **35 on no shift at all** — an Attendance row with no shift cannot be graded, so lateness, hours and
 * overtime are simply absent for them; **19 rostered days sitting in draft** for October, which roster nobody until
 * somebody submits them; and `Shift Request` empty, not from habit but because hrms accepts only the employee's
 * `shift_request_approver` or a `Department Approver` row of their department, and this site has neither — 0 of 137
 * and 0 of 37. The screen says which of the two to fill rather than offering a form that cannot be submitted. */
export default function Roster({ company }: { company: string }) {
  const { t } = useTranslation();
  const [on, setOn] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const roster = useRoster(company || undefined, on || undefined);
  const requests = useShiftRequests(company || undefined);
  const gaps = useAttendanceGaps(range.from, range.to, company || undefined);
  const assign = useAssignShift();
  const endIt = useEndAssignment();
  const publish = usePublishAssignment();
  const decide = useDecideShiftRequest();
  const mark = useMarkAttendance();

  if (roster.isError) return <div className="mt-6"><ErrorBox error={roster.error} onRetry={() => void roster.refetch()} /></div>;

  return (
    <div className="mt-4 space-y-4" data-hr-roster>
      {!roster.data ? (
        <div className="grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-hr-roster-strip>
            <Stat label={t("rst.rostered")} value={`${roster.data.rostered} / ${roster.data.people}`} hint={t("rst.rosteredHint")} />
            <Stat label={t("rst.noShift")} value={roster.data.on_no_shift_count}
              cls={roster.data.on_no_shift_count ? "text-status-amber" : "text-status-green"} hint={t("rst.noShiftHint")} />
            <Stat label={t("rst.drafts")} value={roster.data.draft_count}
              cls={roster.data.draft_count ? "text-status-amber" : ""} hint={t("rst.draftsHint")} />
            <Stat label={t("rst.requests")} value={requests.data?.count ?? "–"} hint={t("rst.requestsHint")} />
          </div>

          <SectionCard title={t("rst.title")} aside={roster.data.on}>
            <div className="flex flex-wrap items-center gap-2 border-b border-brand-grey-light px-4 py-2 text-[12px]">
              <label className="text-brand-grey" htmlFor="roster-on">{t("rst.onDay")}</label>
              <input id="roster-on" type="date" value={on} onChange={(e) => setOn(e.target.value)}
                className="rounded border border-brand-grey-light px-2 py-1" data-hr-roster-day />
            </div>
            <div className="grid gap-4 p-4 md:grid-cols-2 lg:grid-cols-4">
              {Object.entries(roster.data.shifts).map(([shift, people]) => (
                <div key={shift} data-hr-roster-shift={shift}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">
                    {shift} · {people.length}
                  </div>
                  {people.slice(0, 12).map((p) => (
                    <div key={p.assignment ?? p.employee} className="flex items-center justify-between gap-2 truncate text-[12px]">
                      <span className="truncate">{p.employee_name}</span>
                      {roster.data.can_edit && p.assignment && !p.to && (
                        <button data-hr-roster-end={p.assignment} disabled={endIt.isPending}
                          onClick={() => endIt.mutate({ assignment: p.assignment as string, end_date: on || roster.data.on })}
                          className="shrink-0 text-[11px] font-semibold text-brand-primary disabled:opacity-50">
                          {t("rst.end")}
                        </button>
                      )}
                    </div>
                  ))}
                  {people.length > 12 && <div className="text-[11px] text-brand-grey">+{people.length - 12}</div>}
                </div>
              ))}
            </div>
          </SectionCard>

          {roster.data.on_no_shift_count > 0 && (
            <SectionCard title={t("rst.noShiftTitle")} aside={String(roster.data.on_no_shift_count)}>
              <p className="border-b border-brand-grey-light px-4 py-2 text-[12px] text-brand-grey">{t("rst.noShiftWhy")}</p>
              <div className="grid gap-2 p-4 md:grid-cols-2 lg:grid-cols-3">
                {roster.data.on_no_shift.slice(0, 24).map((p) => (
                  <div key={p.employee} className="flex items-center justify-between gap-2 text-[12px]"
                    data-hr-roster-noshift={p.employee}>
                    <span className="truncate">{p.employee_name}<span className="text-brand-grey"> · {p.company}</span></span>
                    {roster.data.can_edit && roster.data.shift_types[0] && (
                      <button data-hr-roster-assign={p.employee} disabled={assign.isPending}
                        onClick={() => assign.mutate({ employee: p.employee, shift_type: roster.data.shift_types[0].name, from_date: on || roster.data.on })}
                        className="shrink-0 rounded bg-brand-primary px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50">
                        {t("rst.assign", { shift: roster.data.shift_types[0].name })}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {roster.data.draft_count > 0 && (
            <SectionCard title={t("rst.draftsTitle")} aside={String(roster.data.draft_count)}>
              <p className="border-b border-brand-grey-light px-4 py-2 text-[12px] text-brand-grey">{t("rst.draftsWhy")}</p>
              <div className="grid gap-2 p-4 md:grid-cols-2 lg:grid-cols-3">
                {roster.data.drafts.slice(0, 24).map((dr) => (
                  <div key={dr.assignment} className="flex items-center justify-between gap-2 text-[12px]"
                    data-hr-roster-draft={dr.assignment}>
                    <span className="truncate">{dr.employee_name}<span className="text-brand-grey"> · {dr.shift_type} · {dr.from}</span></span>
                    {roster.data.can_edit && (
                      <button data-hr-roster-publish={dr.assignment} disabled={publish.isPending}
                        onClick={() => publish.mutate({ assignment: dr.assignment })}
                        className="shrink-0 rounded bg-brand-primary px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50">
                        {t("rst.publish")}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </>
      )}

      <SectionCard title={t("rst.reqTitle")} aside={requests.data ? t("rst.reqN", { n: requests.data.count }) : undefined}>
        {!requests.data ? <div className="p-4"><Skeleton className="h-16" /></div> : requests.data.blocked_reason ? (
          <div className="p-4 text-[12px] text-status-amber" data-hr-roster-req-blocked>
            <p>{t("rst.reqBlocked", { people: requests.data.people, departments: requests.data.departments })}</p>
            <p className="mt-2 text-brand-grey">{requests.data.blocked_reason}</p>
          </div>
        ) : requests.data.rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-hr-roster-req-empty>{t("rst.reqEmpty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("wfa.person")}</th>
                  <th className="px-4 py-2">{t("rst.shift")}</th>
                  <th className="px-4 py-2">{t("rst.from")}</th>
                  <th className="px-4 py-2">{t("ops.status")}</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {requests.data.rows.map((r) => (
                  <tr key={r.name} className="border-t border-brand-grey-light" data-hr-roster-req={r.name}>
                    <td className="px-4 py-2">{r.employee_name}</td>
                    <td className="px-4 py-2">{r.shift_type}</td>
                    <td className="px-4 py-2">{r.from_date}{r.to_date ? ` → ${r.to_date}` : ""}</td>
                    <td className="px-4 py-2">{r.status}</td>
                    <td className="px-4 py-2 text-right">
                      {requests.data.can_decide && r.docstatus === 0 && (
                        <>
                          <button data-hr-roster-approve={r.name} disabled={decide.isPending}
                            onClick={() => decide.mutate({ request: r.name, approve: true })}
                            className="rounded bg-brand-primary px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50">
                            {t("rst.approve")}
                          </button>
                          <button data-hr-roster-reject={r.name} disabled={decide.isPending}
                            onClick={() => decide.mutate({ request: r.name, approve: false })}
                            className="ml-2 text-[11px] font-semibold text-brand-grey disabled:opacity-50">
                            {t("rst.reject")}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title={t("rst.gapTitle")}
        aside={gaps.data ? t("rst.gapN", { n: gaps.data.would_mark, skip: gaps.data.would_skip }) : undefined}>
        <div className="flex flex-wrap items-center gap-2 border-b border-brand-grey-light px-4 py-2 text-[12px]">
          <label className="text-brand-grey" htmlFor="gap-from">{t("rst.from")}</label>
          <input id="gap-from" type="date" value={range.from} data-hr-roster-gap-from
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            className="rounded border border-brand-grey-light px-2 py-1" />
          <label className="text-brand-grey" htmlFor="gap-to">{t("rst.to")}</label>
          <input id="gap-to" type="date" value={range.to} data-hr-roster-gap-to
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            className="rounded border border-brand-grey-light px-2 py-1" />
        </div>
        <p className="px-4 py-2 text-[12px] text-brand-grey">{t("rst.gapHint")}</p>
        {!range.from || !range.to ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-hr-roster-gap-idle>{t("rst.gapPick")}</div>
        ) : !gaps.data ? (
          <div className="p-4"><Skeleton className="h-16" /></div>
        ) : (
          <div className="p-4 text-[12px]" data-hr-roster-gap-plan>
            <div className="flex flex-wrap gap-4">
              <span><strong>{gaps.data.would_mark}</strong> {t("rst.wouldMark")}</span>
              <span className="text-brand-grey"><strong>{gaps.data.would_skip}</strong> {t("rst.wouldSkip")}</span>
              {Object.entries(gaps.data.skipped_by_reason).map(([why, n]) => (
                <span key={why} className="text-brand-grey">{t(`rst.skip.${why}`)}: {n}</span>
              ))}
            </div>
            {gaps.data.can_mark && gaps.data.would_mark > 0 && (
              <button data-hr-roster-mark disabled={mark.isPending}
                onClick={() => mark.mutate({ from_date: range.from, to_date: range.to, company: company || undefined, status: "Present" })}
                className="mt-3 rounded bg-brand-primary px-3 py-1 text-[11px] font-semibold text-white disabled:opacity-50">
                {t("rst.markPresent", { n: gaps.data.would_mark })}
              </button>
            )}
            {mark.data && (
              <p className="mt-2 text-status-green" data-hr-roster-marked>
                {t("rst.marked", { n: mark.data.marked, failed: mark.data.failures })}
              </p>
            )}
          </div>
        )}
      </SectionCard>

      {[assign, endIt, publish, decide, mark].map((m, i) => m.isError ? <ErrorBox key={i} error={m.error} /> : null)}
    </div>
  );
}
